// tina/config.ts
import { defineConfig } from "tinacms";

// tina/collections/blog.ts
var BlogCollection = {
  name: "blog",
  label: "Blogs",
  path: "src/content/blog",
  format: "mdx",
  ui: {
    router({ document }) {
      return `/blog/${document._sys.filename}`;
    }
  },
  fields: [
    {
      type: "string",
      name: "title",
      label: "Title",
      isTitle: true,
      required: true
    },
    {
      name: "description",
      label: "Description",
      type: "string"
    },
    {
      name: "pubDate",
      label: "Publication Date",
      type: "datetime",
      required: true
    },
    {
      name: "updatedDate",
      label: "Updated Date",
      type: "datetime"
    },
    {
      name: "heroImage",
      label: "Hero Image",
      type: "image",
      required: true
    },
    {
      name: "category",
      label: "Category",
      type: "string",
      options: [
        "Technology",
        "Food",
        "Lifestyle",
        "Travel",
        "Health",
        "Education"
      ],
      required: true
    },
    {
      type: "rich-text",
      name: "body",
      label: "Body",
      isBody: true
    }
  ]
};

// tina/components/IconComponent.tsx
import React, { useState, useRef, useEffect } from "react";
import { wrapFieldsWithMeta } from "tinacms";
import * as tb from "react-icons/tb";
import { useVirtualizer } from "@tanstack/react-virtual";
var IconComponent = wrapFieldsWithMeta((props) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [containerWidth, setContainerWidth] = useState(0);
  const iconName = props.input.value;
  const iconSize = 50;
  const gapSize = 10;
  const parentRef = useRef(null);
  const filteredIcons = Object.keys(tb).filter(
    (iconKey) => iconKey.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const itemsPerRow = Math.max(
    1,
    Math.floor((containerWidth + gapSize) / (iconSize + gapSize))
  );
  const totalRows = Math.ceil(filteredIcons.length / itemsPerRow);
  const rowVirtualizer = useVirtualizer({
    count: totalRows,
    getScrollElement: () => parentRef.current,
    estimateSize: () => iconSize + gapSize,
    overscan: 5
  });
  useEffect(() => {
    const updateWidth = () => {
      if (parentRef.current) {
        setContainerWidth(parentRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);
  const handleIconSelect = (name) => {
    props.input.onChange(name);
  };
  return React.createElement("div", null, React.createElement("div", { style: { marginBottom: "1rem" } }, React.createElement("strong", null, "Selected Icon:"), " ", iconName ? React.createElement(tb[iconName], { size: 24 }) : "None"), React.createElement("div", { style: { marginBottom: "1rem" } }, React.createElement(
    "input",
    {
      type: "text",
      placeholder: "Search icons...",
      value: searchQuery,
      onChange: (e) => setSearchQuery(e.target.value),
      style: {
        width: "100%",
        padding: "8px",
        border: "1px solid #ddd",
        borderRadius: "4px"
      }
    }
  )), React.createElement(
    "div",
    {
      ref: parentRef,
      style: {
        maxHeight: "300px",
        overflowY: "auto",
        border: "1px solid #ddd",
        padding: "10px",
        borderRadius: "4px",
        position: "relative"
      }
    },
    React.createElement(
      "div",
      {
        style: {
          height: `${rowVirtualizer.getTotalSize()}px`,
          position: "relative"
        }
      },
      rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const startIndex = virtualRow.index * itemsPerRow;
        const endIndex = Math.min(
          startIndex + itemsPerRow,
          filteredIcons.length
        );
        return React.createElement(
          "div",
          {
            key: virtualRow.key,
            style: {
              position: "absolute",
              top: `${virtualRow.start}px`,
              left: 0,
              display: "flex",
              gap: `${gapSize}px`
            }
          },
          filteredIcons.slice(startIndex, endIndex).map((iconKey) => {
            const Icon = tb[iconKey];
            const isSelected = iconKey === iconName;
            return React.createElement(
              "div",
              {
                key: iconKey,
                onClick: () => handleIconSelect(iconKey),
                style: {
                  cursor: "pointer",
                  padding: "5px",
                  border: isSelected ? "2px solid blue" : "2px solid transparent",
                  borderRadius: "4px",
                  textAlign: "center",
                  width: `${iconSize}px`,
                  height: `${iconSize}px`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }
              },
              React.createElement(Icon, { size: 24 })
            );
          })
        );
      })
    )
  ));
});
var IconComponent_default = IconComponent;

// tina/collections/global-config.ts
var GlobalConfigCollection = {
  name: "config",
  label: "Global Config",
  path: "src/content/config",
  format: "json",
  ui: {
    global: true
  },
  fields: [
    {
      name: "seo",
      label: "General site config",
      type: "object",
      fields: [
        {
          name: "title",
          label: "Site title for SEO",
          type: "string",
          required: true
        },
        {
          name: "description",
          label: "Site description for SEO",
          type: "string",
          required: true
        },
        {
          name: "siteOwner",
          label: "Your Name, Company Name (Used in the footer",
          required: true,
          type: "string",
          ui: {
            defaultValue: "Your name here"
          }
        }
        // Add more settings here...
      ]
    },
    {
      name: "nav",
      label: "Site Navigation Menu (Reorder, Add, Remove)",
      type: "object",
      list: true,
      ui: {
        itemProps: (item) => {
          return {
            label: item.title
          };
        }
      },
      fields: [
        {
          name: "title",
          label: "Title of Nav Item",
          type: "string",
          required: true
        },
        {
          name: "link",
          label: "Path of the Nav Item",
          type: "string",
          required: true
        }
      ]
    },
    {
      name: "contactLinks",
      label: "Contact Links",
      type: "object",
      list: true,
      ui: {
        itemProps: (item) => {
          return {
            label: item.title
          };
        }
      },
      fields: [
        {
          name: "title",
          label: "Title",
          type: "string"
        },
        {
          name: "link",
          label: "Link",
          type: "string"
        },
        {
          name: "icon",
          label: "Icon",
          type: "string",
          ui: {
            //@ts-ignore
            component: IconComponent_default
          }
        }
      ]
    }
    // Add other config fields here...
  ]
};

// tina/collections/page.ts
var PageCollection = {
  name: "page",
  label: "Pages",
  path: "src/content/page",
  format: "mdx",
  ui: {
    router: ({ document }) => {
      return `/${document._sys.filename}`;
    }
  },
  fields: [
    {
      name: "seoTitle",
      label: "Page Title",
      type: "string",
      required: true
    },
    {
      name: "description",
      label: "Page Description",
      type: "string"
    },
    {
      name: "heroImage",
      label: "Hero Image",
      type: "image"
    },
    {
      name: "body",
      label: "Content",
      type: "rich-text",
      isBody: true,
      required: true
    }
  ]
};

// tina/config.ts
var branch = process.env.GITHUB_BRANCH || process.env.VERCEL_GIT_COMMIT_REF || process.env.HEAD || "main";
var config_default = defineConfig({
  branch,
  // Get this from tina.io
  clientId: process.env.PUBLIC_TINA_CLIENT_ID,
  // Get this from tina.io
  token: process.env.TINA_TOKEN,
  build: {
    outputFolder: "admin",
    publicFolder: "public"
  },
  media: {
    tina: {
      mediaRoot: "",
      publicFolder: "public"
    }
  },
  // See docs on content modeling for more info on how to setup new content models: https://tina.io/docs/schema/
  schema: {
    collections: [BlogCollection, PageCollection, GlobalConfigCollection]
  }
});
export {
  config_default as default
};
